import React from "react";

export enum ScrollPosition {
  Start = "start",
  Middle = "middle",
  End = "end"
}

type ScrollDirection = "horizontal" | "vertical";

interface UseScrollObserverOptions {
  isScrolledToEndThresholdInPx?: number;
  shouldObserveResizeEvents?: boolean;
  deps?: React.DependencyList;
}

interface UseScrollObserverReturn {
  scrollPosition: Record<ScrollDirection, ScrollPosition>;
  isScrollable: Record<ScrollDirection, boolean>;
}

const evalIsVerticallyScrollable = ({ scrollHeight, clientHeight }: HTMLElement): boolean =>
  scrollHeight > clientHeight;

const evalIsHorizontallyScrollable = ({ scrollWidth, clientWidth }: HTMLElement): boolean => scrollWidth > clientWidth;

// Keeping the previous state object when nothing changed lets React skip the re-render.
const keepIfEqual =
  <T>(next: Record<ScrollDirection, T>) =>
  (previous: Record<ScrollDirection, T>): Record<ScrollDirection, T> =>
    previous.horizontal === next.horizontal && previous.vertical === next.vertical ? previous : next;

const evalVerticalScrollPosition = (
  { scrollHeight, scrollTop, offsetHeight }: HTMLElement,
  isScrolledToEndThresholdInPx: UseScrollObserverOptions["isScrolledToEndThresholdInPx"]
): ScrollPosition => {
  const scrolledHeight = scrollTop + offsetHeight;
  const remainingScrollHeight = Math.abs(scrollHeight - scrolledHeight);

  const isScrolledToStart = scrollTop === 0;
  const isScrolledToEnd = remainingScrollHeight <= (isScrolledToEndThresholdInPx ?? 0);

  return isScrolledToStart ? ScrollPosition.Start : isScrolledToEnd ? ScrollPosition.End : ScrollPosition.Middle;
};

const evalHorizontalScrollPosition = (
  { scrollWidth, scrollLeft, offsetWidth }: HTMLElement,
  isScrolledToEndThresholdInPx: UseScrollObserverOptions["isScrolledToEndThresholdInPx"]
): ScrollPosition => {
  const scrolledWidth = scrollLeft + offsetWidth;
  const remainingScrollWidth = Math.abs(scrollWidth - scrolledWidth);

  const isScrolledToStart = scrollLeft === 0;
  const isScrolledToEnd = remainingScrollWidth <= (isScrolledToEndThresholdInPx ?? 0);

  return isScrolledToStart ? ScrollPosition.Start : isScrolledToEnd ? ScrollPosition.End : ScrollPosition.Middle;
};

type ScrollableElementEvaluatedHandler = (element: HTMLElement) => void;

const useScrollability = (
  elementRef: React.RefObject<HTMLElement | null>,
  {
    shouldObserveResizeEvents = false,
    deps = []
  }: Pick<UseScrollObserverOptions, "shouldObserveResizeEvents" | "deps">,
  onScrollableElementEvaluated?: ScrollableElementEvaluatedHandler
): Record<ScrollDirection, boolean> => {
  const frameID = React.useRef<number>(0);

  const [isScrollable, setIsScrollable] = React.useState<UseScrollObserverReturn["isScrollable"]>({
    horizontal: false,
    vertical: false
  });

  // Resizes and content changes can move the scroll position without firing a scroll event,
  // so the scroll position is evaluated together with the scrollability.
  const evalIsScrollable = React.useCallback(
    (element: HTMLElement): void => {
      const nextIsScrollable = {
        vertical: evalIsVerticallyScrollable(element),
        horizontal: evalIsHorizontallyScrollable(element)
      };
      setIsScrollable(keepIfEqual(nextIsScrollable));

      if (nextIsScrollable.vertical || nextIsScrollable.horizontal) {
        onScrollableElementEvaluated?.(element);
      }
    },
    [onScrollableElementEvaluated]
  );

  // After mount, initially evaluate scrollability, and then observe resize events of element.
  // The ResizeObserver is created inside the effect (rather than via useMemo) so its
  // callback doesn't read refs during render — see `react-hooks/refs`.
  React.useEffect(() => {
    const element = elementRef?.current;

    /* Why cancelAnimationFrame()?
     * Because the resizeObserver fires before the element is actually resized, so we need to wait for the element to be resized before we can call evalIsScrollable().
     * These next lines prevent evalIsScrollable() from being called before the element is actually resized, and improves performance.
     * Modeled after: https://github.com/mantinedev/mantine/blob/master/src/mantine-hooks/src/use-resize-observer/use-resize-observer.ts
     */
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        cancelAnimationFrame(frameID.current);

        frameID.current = requestAnimationFrame(() => {
          if (elementRef.current) {
            evalIsScrollable(elementRef.current);
          }
        });
      }
    });

    if (element) {
      evalIsScrollable(element);
      if (shouldObserveResizeEvents) {
        resizeObserver.observe(element);
      }
    }

    return () => {
      if (element && shouldObserveResizeEvents) {
        resizeObserver.unobserve(element);
      }
      if (frameID.current) {
        cancelAnimationFrame(frameID.current);
      }
    };

    // Rule disabled only for spread deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, evalIsScrollable, shouldObserveResizeEvents, ...deps]);

  return isScrollable;
};

export const useIsScrollable = (
  elementRef: React.RefObject<HTMLElement | null>,
  options: Pick<UseScrollObserverOptions, "shouldObserveResizeEvents" | "deps"> = {}
): Record<ScrollDirection, boolean> => useScrollability(elementRef, options);

const useScrollPositionState = (
  isScrolledToEndThresholdInPx: UseScrollObserverOptions["isScrolledToEndThresholdInPx"]
): [Record<ScrollDirection, ScrollPosition>, ScrollableElementEvaluatedHandler] => {
  const [scrollPosition, setScrollPosition] = React.useState<UseScrollObserverReturn["scrollPosition"]>({
    horizontal: ScrollPosition.Start,
    vertical: ScrollPosition.Start
  });

  const evalScrollPosition = React.useCallback(
    (element: HTMLElement): void => {
      setScrollPosition(
        keepIfEqual({
          horizontal: evalHorizontalScrollPosition(element, isScrolledToEndThresholdInPx),
          vertical: evalVerticalScrollPosition(element, isScrolledToEndThresholdInPx)
        })
      );
    },
    [isScrolledToEndThresholdInPx]
  );

  return [scrollPosition, evalScrollPosition];
};

// While the element is scrollable, re-evaluate the scroll position on every scroll event.
const useScrollListener = (
  elementRef: React.RefObject<HTMLElement | null>,
  { horizontal: isHorizontallyScrollable, vertical: isVerticallyScrollable }: Record<ScrollDirection, boolean>,
  evalScrollPosition: ScrollableElementEvaluatedHandler
): void => {
  React.useEffect(() => {
    const element = elementRef?.current;
    if (!element || !(isHorizontallyScrollable || isVerticallyScrollable)) {
      return undefined;
    }

    const handleScrollEvent = (): void => evalScrollPosition(element);
    element.addEventListener("scroll", handleScrollEvent);

    return () => element.removeEventListener("scroll", handleScrollEvent);
  }, [elementRef, isHorizontallyScrollable, isVerticallyScrollable, evalScrollPosition]);
};

export default (
  elementRef: React.RefObject<HTMLElement | null>,
  { isScrolledToEndThresholdInPx = 30, shouldObserveResizeEvents = false, deps = [] }: UseScrollObserverOptions = {}
): UseScrollObserverReturn => {
  const [scrollPosition, evalScrollPosition] = useScrollPositionState(isScrolledToEndThresholdInPx);
  const isScrollable = useScrollability(elementRef, { shouldObserveResizeEvents, deps }, evalScrollPosition);
  useScrollListener(elementRef, isScrollable, evalScrollPosition);

  return {
    scrollPosition,
    isScrollable
  };
};
